import async from 'async';
import { isEmpty } from 'lodash-es';
import { useApiService } from '../../../../contexts/ApiServiceContext/ApiServiceContext';
import {
  useCharacterExpressionsContext,
} from '../../../../contexts/CharacterExpressionsContext/CharacterExpressionsContext';
import { BookLocation } from '../../../../dorian-shared/types/bookLocation/bookLocation';
import { AnswerStep, AnswerStepModifier, AnswerTypeId } from '../../../../dorian-shared/types/branch/AnswerStep';
import { Branch } from '../../../../dorian-shared/types/branch/Branch';
import { BranchStep, BranchStepMemoryAction, BranchStepMemoryActionType, BranchStepMemoryRollType } from '../../../../dorian-shared/types/branch/BranchStep';
import { Character } from '../../../../dorian-shared/types/character/Character';
import { EpisodeTextImportHelper } from '../../../../services/EpisodeTextService/models/EpisodeTextImportHelper';
import { EpisodeTextImportNode } from '../../../../services/EpisodeTextService/models/EpisodeTextImportNode';
import { parseReqCommand } from '../../../../services/EpisodeTextService/models/parseReqCommand';
import { EpisodeTextImportCommandKey, TEpisodeTextImportCommands } from '../../../../services/EpisodeTextService/types/episodeTextImportCommandTypes';
import { resolveExpressionId } from '../../../../services/EpisodeTextService/utils/resolveExpressionId';
import { EpisodeTextEntityType } from '../../../../services/EpisodeTextService/types/episodeTextImportTypes';
import { logger } from '../../../../services/loggerService/loggerService';
import { showToast } from '../../../ui/utils';
import {
  CheckOperator,
  MemoryCheckValueType,
  MemoryDTO,
  MemorySingleLink,
  MemoryType,
  StepCheck,
  StepCheckOperatorType,
  StepCheckSwitch,
} from '../../Book/MemoryBank/memoryBankTypes';
import { createNodeTree } from '../textImportUtils';

type ResolvedSpeaker = {
  characterId: number | undefined;
  speakerMemoryId: number | undefined;
};

export function useImportTextNodeManage(
  nodes: Branch[],
  bookLocations: BookLocation[] | null,
  episodeId: number,
  characters: Character[] | null,
  memoryBank: MemoryDTO[] | null,
) {
  const apiService = useApiService();
  const possibleExpressions = useCharacterExpressionsContext();

  const getExpressionId = (stepCommands: TEpisodeTextImportCommands) =>
    resolveExpressionId(stepCommands, possibleExpressions);

  const resolveSpeaker = (step: { speakerMemoryId?: number; speakerName: string }): ResolvedSpeaker => {
    if (step.speakerMemoryId != null) {
      return { characterId: undefined, speakerMemoryId: step.speakerMemoryId };
    }
    const cleaned = EpisodeTextImportHelper.removeAllCommandsFromString(step.speakerName).toLocaleLowerCase();
    const character = characters?.find((el) => el.alias.toLocaleLowerCase() === cleaned);
    return { characterId: character?.id, speakerMemoryId: undefined };
  };

  const MAX_CONCURRENT_POST_REQUESTS = 5;
  const MAX_CONCURRENT_PUT_REQUESTS = 1;

  async function createEpisodeNodes(episodeTextImportNodes: EpisodeTextImportNode[]) {
    const lowestNodeY = EpisodeTextImportHelper.getLowestYInNodes(nodes);
    let nodePosition = {
      x: 1000,
      y: lowestNodeY + 350,
    };

    const createdNodes: Branch[] = [];
    const createNodePromises: (() => Promise<Partial<Branch>>)[] = [];

    for (let nodeIndex = 0; nodeIndex < episodeTextImportNodes.length; nodeIndex++) {
      const episodeTextImportNode = episodeTextImportNodes[nodeIndex];
      const nodeTitle = EpisodeTextImportHelper.removeAllCommandsFromString(episodeTextImportNode.title);
      const nodeDescription = EpisodeTextImportHelper.removeAllCommandsFromString(episodeTextImportNode.description);

      nodePosition = {
        ...nodePosition,
        y: nodePosition.y + 350,
      };

      const locationCommand = episodeTextImportNode.commands.get(EpisodeTextImportCommandKey.Location);
      const locationCommandValue = locationCommand?.values().next().value?.[2] ?? '';
      const locationNameNormalized = locationCommandValue.toLocaleLowerCase().trim();
      const location = bookLocations?.find(
        (el) => el.title.toLocaleLowerCase().trim() === locationNameNormalized
         || el.alias.toLocaleLowerCase().trim() === locationNameNormalized,
      );

      const isIntroNode = episodeTextImportNode.title.toLocaleLowerCase() === 'intro';
      if (isIntroNode) {
        const introNode = nodes.find((el) => el.title.toLocaleLowerCase() === 'intro');
        if (introNode) {
          const newIntroNode = {
            ...introNode,
            description: nodeDescription,
            locationId: introNode.locationId,
          };
          createdNodes.push(newIntroNode);
        }
      } else {
        const nodeToCreate = {
          number: 1,
          title: nodeTitle,
          description: nodeDescription,
          locationId: location?.id,
          ...nodePosition,
        };
        createNodePromises.push(async () => await apiService.createEpisodeNode(episodeId, nodeToCreate));
      }
    }
    try {
      const createNodeQueue = createNodePromises.map(
        (createNodePromise) => (callback: (error: unknown | null, result: Partial<Branch> | null) => void) => {
          createNodePromise()
            .then((response) => {
              callback(null, response);
            })
            .catch((error) => callback(error, null));
        },
      );
      const newNodes: Branch[] = await async.parallelLimit(createNodeQueue, MAX_CONCURRENT_POST_REQUESTS);
      createdNodes.push(...newNodes as Branch[]);
    } catch (error) {
      logger.error(`Error in creating branch: ${error}`);
      showToast({ textMessage: `Error in creating branch: ${error}` });
    }
    return createdNodes;
  }

  async function updateEpisodeNodes(createdNodes: Branch[], episodeTextImportNodes: EpisodeTextImportNode[]) {
    const updatedNodes: Branch[] = [];
    const updateNodesPromises: (() => Promise<Partial<Branch>>)[] = [];

    const lowestNodeY = EpisodeTextImportHelper.getLowestYInNodes(nodes);
    let firstUnlinkedNodePositionY = lowestNodeY + 350;
    const allNodes = [...nodes, ...createdNodes];
    const resolvedNodes: { nodeToUpdate: Partial<Branch>; createdNodeId: number }[] = [];

    for (let nodeIndex = 0; nodeIndex < createdNodes.length; nodeIndex++) {
      const createdNode = createdNodes[nodeIndex];
      const isLastNode = nodeIndex === createdNodes.length - 1;

      let nodeToUpdate: Partial<Branch> = {
        id: createdNode.id,
        title: createdNode.title,
        description: createdNode.description,
        locationId: createdNode.locationId,
      };

      const nodeToCreate = episodeTextImportNodes.find(
        (el) => el.title.toLocaleLowerCase() === createdNode.title.toLocaleLowerCase(),
      );
      if (!nodeToCreate) {
        showToast({ textMessage: `Node not found: ${createdNode.title}` });
        logger.error(`[ImportTextModal] Node not found: ${createdNode.title}`);
        // eslint-disable-next-line no-continue
        continue;
      }

      const { steps } = nodeToCreate;

      if (steps.length === 0 && !isLastNode) {
        const nextNode = createdNodes[nodeIndex + 1];
        nodeToUpdate = {
          ...nodeToUpdate,
          gotoBranchId: nextNode.id,
        };
      }

      const stepsToCreate: Partial<BranchStep>[] = [];

      for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) {
        let stepToCreate: Partial<BranchStep> = {};
        const step = steps[stepIndex];
        const isLastStep = stepIndex === steps.length - 1;
        const isEnding = step.type === EpisodeTextEntityType.Ending;
        const stepTypeId = EpisodeTextImportHelper.getStepTypeIdByEntityType(step.type);
        const speakerName = EpisodeTextImportHelper.removeAllCommandsFromString(step.speakerName);
        const speakerText = EpisodeTextImportHelper.removeAllCommandsFromString(step.speakerText);
        if (step.backgroundLocationId != null) stepToCreate = { ...stepToCreate, backgroundLocationId: step.backgroundLocationId };

        switch (step.type) {
          case EpisodeTextEntityType.GotoFromNode: {
            const gotoNodeCommand = step.commands.get(EpisodeTextImportCommandKey.Goto);
            const toNodeTitle = String(gotoNodeCommand?.values().next().value?.[2] ?? '');

            const toNode = allNodes.find((el) => el.title?.toLocaleLowerCase() === toNodeTitle?.toLocaleLowerCase());
            if (!toNode) {
              logger.error('[ImportTextModal] To node not found');
              // eslint-disable-next-line no-continue
              continue;
            }
            nodeToUpdate = {
              ...nodeToUpdate,
              gotoBranchId: toNode.id,
            };
            // eslint-disable-next-line no-continue
            continue;
          }
          case EpisodeTextEntityType.Chat: {
            const commands = EpisodeTextImportHelper.convertCommandsToRecordType(step.commands);
            const speaker = resolveSpeaker(step);
            stepToCreate = {
              ...stepToCreate,
              stepTypeId,
              text: speakerText,
              characterId: speaker.characterId,
              speakerMemoryId: speaker.speakerMemoryId,
              characterExpressionId: commands.anxious.size > 0
                ? possibleExpressions.find((el) => el.value === EpisodeTextImportCommandKey.ExpressionAnxious)?.id
                : undefined,
            };
            break;
          }
          case EpisodeTextEntityType.Dialogue: {
            const speaker = resolveSpeaker(step);
            stepToCreate = {
              ...stepToCreate,
              stepTypeId,
              text: speakerText,
              characterId: speaker.characterId,
              speakerMemoryId: speaker.speakerMemoryId,
              characterExpressionId: getExpressionId(step.commands),
            };
            break;
          }
          case EpisodeTextEntityType.Choice: {
            const speaker = resolveSpeaker(step);

            const answers: Partial<AnswerStep>[] = [];
            let answerStepIndex = stepIndex + 1;
            let nextStepAnswer = steps[answerStepIndex];

            while (nextStepAnswer?.type === EpisodeTextEntityType.Answer) {
              const answerText = EpisodeTextImportHelper.removeAllCommandsFromString(nextStepAnswer.speakerText);
              const gotoNodeCommand = nextStepAnswer.commands.get(EpisodeTextImportCommandKey.Goto);
              const gotoBranchName = gotoNodeCommand?.values().next().value?.[2];
              const gotoBranchId = gotoBranchName
                ? allNodes.find((el) => el.title?.toLocaleLowerCase() === gotoBranchName?.toLowerCase())?.id
                : undefined;

              const isPremiumAnswer = nextStepAnswer.commands.has(EpisodeTextImportCommandKey.PremiumAnswer);
              const premiumAnswerCommand = nextStepAnswer.commands.get(EpisodeTextImportCommandKey.PremiumAnswer);
              const answerPrice = premiumAnswerCommand?.values().next().value?.[2] ?? 0;

              const isBonusContent = nextStepAnswer.commands.has(EpisodeTextImportCommandKey.BonusContent);
              const bonusModifier: AnswerStepModifier | undefined = isBonusContent
                ? { answerModifierTypeId: 1 }
                : undefined;

              // Build requirement from #REQ command
              const reqCommand = nextStepAnswer.commands.get(EpisodeTextImportCommandKey.StepReq);
              let requirement: { check: StepCheck; visualTreatmentIfNotMet: string; isBadgeHidden: boolean } | undefined;
              if (reqCommand && reqCommand.size > 0) {
                const reqValues = reqCommand.values().next().value;
                const remainder = reqValues?.[2]?.trim() ?? '';
                const { reqMemoryName, reqOperatorSymbol, reqValue, reqOtherwise, reqHideIndicator } = parseReqCommand(remainder);

                // reqMemoryName / reqOperatorSymbol are guaranteed non-empty here:
                // _validateReqCommand already rejected the step if the main part didn't match.
                const reqMemory = memoryBank?.find((el) => el.name.toLocaleLowerCase().trim() === reqMemoryName);
                if (reqMemory) {
                  const operators = [
                    StepCheckOperatorType.Equal,
                    StepCheckOperatorType.NotEqual,
                    StepCheckOperatorType.Greater,
                    StepCheckOperatorType.AtLeast,
                    StepCheckOperatorType.Less,
                    StepCheckOperatorType.AtMost,
                  ];
                  const reqOperator = operators.find((op) => CheckOperator[op].short === reqOperatorSymbol);

                  if (!reqOperator) {
                    logger.error(`[ImportTextModal] REQ: Unrecognized operator: ${reqOperatorSymbol}`);
                  } else {
                    // Determine if value is a variable reference
                    const compareMemory = memoryBank?.find(
                      (el) => el.name.toLocaleLowerCase().trim() === reqValue.toLocaleLowerCase().trim(),
                    );

                    let checkValue: string | MemorySingleLink;
                    if (compareMemory) {
                      checkValue = {
                        type: MemoryCheckValueType.Variable,
                        variableId: compareMemory.id.toString(),
                      };
                    } else if (reqMemory.type === MemoryType.Boolean) {
                      checkValue = String(reqValue.toLocaleLowerCase() === 'yes');
                    } else if (reqMemory.type === MemoryType.Character) {
                      const matchedCharacter = characters?.find(
                        (c) => c.alias?.toLowerCase() === reqValue.toLowerCase(),
                      );
                      checkValue = matchedCharacter ? String(matchedCharacter.id) : reqValue;
                    } else {
                      checkValue = reqValue;
                    }

                    requirement = {
                      check: {
                        variableId: reqMemory.id,
                        operator: reqOperator,
                        value: checkValue,
                      },
                      visualTreatmentIfNotMet: reqOtherwise.toLowerCase() === 'disable' ? 'Disable' : 'Hide',
                      isBadgeHidden: /^hide/i.test(reqHideIndicator),
                    };
                  }
                }
              }

              answers.push({
                text: answerText,
                answerTypeId: isPremiumAnswer ? AnswerTypeId.Paid : AnswerTypeId.Free,
                answerPrice: Number(answerPrice),
                gotoBranchId,
                modifiers: bonusModifier ? [bonusModifier] : undefined,
                ...(requirement && { requirement }),
              });
              answerStepIndex += 1;
              nextStepAnswer = steps[answerStepIndex];
            }
            stepIndex = answerStepIndex - 1;
            const choiceText = EpisodeTextImportHelper.removeAllCommandsFromString(step.speakerText);
            stepToCreate = {
              ...stepToCreate,
              stepTypeId,
              text: choiceText,
              characterId: speaker.characterId,
              speakerMemoryId: speaker.speakerMemoryId,
              characterExpressionId: getExpressionId(step.commands),
              answers: answers as AnswerStep[],
            };
            stepsToCreate.push(stepToCreate);
            // eslint-disable-next-line no-continue
            continue;
          }
          case EpisodeTextEntityType.Answer: {
            logger.error(`[ImportTextModal] Answer step can be implemented in Choice step: ${step.speakerText}`);
            break;
          }
          case EpisodeTextEntityType.Thinking:
          case EpisodeTextEntityType.Reaction: {
            const speaker = resolveSpeaker(step);
            stepToCreate = {
              ...stepToCreate,
              stepTypeId,
              text: speakerText,
              characterId: speaker.characterId,
              speakerMemoryId: speaker.speakerMemoryId,
              characterExpressionId: getExpressionId(step.commands),
            };
            break;
          }
          case EpisodeTextEntityType.Remember: {
            if (step.action) {
              const action = step.action;
              const targetMemory = memoryBank?.find((m) => Number(m.id) === Number(action.variableId));
              let resolvedAction: BranchStepMemoryAction = action;
              if (
                targetMemory?.type === MemoryType.Character
                && action.type === BranchStepMemoryActionType.Set
                && typeof action.value === 'string'
              ) {
                const matchedCharacter = characters?.find(
                  (c) => c.alias?.toLowerCase() === String(action.value).toLowerCase(),
                );
                if (matchedCharacter) {
                  resolvedAction = { ...action, value: Number(matchedCharacter.id) };
                }
              }
              stepToCreate = {
                ...stepToCreate,
                stepTypeId,
                action: resolvedAction,
              };
            } else {
              showToast({ textMessage: `Remember command action not found: ${createdNode.title}` });
              logger.error(`[ImportTextModal] Remember command action not found: ${createdNode.title}`);
            }
            break;
          }
          case EpisodeTextEntityType.Random: {
            const randomCommand = step.commands.get(EpisodeTextImportCommandKey.StepRandom);
            if (!randomCommand || randomCommand.size === 0) {
              logger.error(`[ImportTextModal] Random command not found: ${createdNode.title}`);
              break;
            }
            
            const variableName = randomCommand.values().next().value?.[2]?.toLocaleLowerCase().trim();
            const operator = randomCommand.values().next().value?.[3]?.toLocaleLowerCase().trim();
            
            const memory = memoryBank?.find((el) => el.name.toLocaleLowerCase().trim() === variableName);
            if (!memory) {
              showToast({ textMessage: `Variable not found: ${variableName}` });
              logger.error(`[ImportTextModal] Variable not found: ${variableName}`, memoryBank);
              break;
            }

            const variableId = memory?.id ?? 0;

            // Check if this is dice notation (operator will be 'dice')
            if (operator === 'dice') {
              const diceNotation = randomCommand.values().next().value?.[4]?.trim() || '1d6';
              
              stepToCreate = {
                ...stepToCreate,
                stepTypeId,
                roll: {
                  variableId,
                  type: BranchStepMemoryRollType.Dice,
                  diceNotation,
                },
              };
            } else {
              // Range-based random: [min, max] with optional modifier
              const minValue = randomCommand.values().next().value?.[4]?.trim();
              const maxValue = randomCommand.values().next().value?.[5]?.trim();
              const modifierValue = randomCommand.values().next().value?.[6]?.trim();
              
              const min: string = minValue ?? '';
              const max: string = maxValue ?? '';
              const modifier = modifierValue !== undefined && modifierValue !== '' ? Number(modifierValue) : undefined;

              switch (operator) {
                case '+':
                  stepToCreate = {
                    ...stepToCreate,
                    stepTypeId,
                    roll: {
                      variableId,
                      type: BranchStepMemoryRollType.Increase,
                      min,
                      max,
                      ...(modifier !== undefined && { modifier }),
                    },
                  };
                  break;
                case '-':
                  stepToCreate = {
                    ...stepToCreate,
                    stepTypeId,
                    roll: {
                      variableId,
                      type: BranchStepMemoryRollType.Decrease,
                      min,
                      max,
                      ...(modifier !== undefined && { modifier }),
                    },
                  };
                  break;
                case '=':
                  stepToCreate = {
                    ...stepToCreate,
                    stepTypeId,
                    roll: {
                      variableId,
                      type: BranchStepMemoryRollType.Set,
                      min,
                      max,
                      ...(modifier !== undefined && { modifier }),
                    },
                  };
                  break;
                default:
                  showToast({ textMessage: `Random command roll type not found: ${createdNode.title}` });
                  logger.error(`[ImportTextModal] Random command roll type not found: ${createdNode.title}`);
                  break;
              }
            }
            break;
          }
          case EpisodeTextEntityType.CheckConditionList:
          case EpisodeTextEntityType.Check: {
            const checkCommand = step.commands.get(EpisodeTextImportCommandKey.StepCheck);
            if (!checkCommand || checkCommand.size === 0) {
              logger.error(`[ImportTextModal] Check command not found: ${createdNode.title}`);
              break;
            }
            if (!step.check || !step.switch) {
              showToast({ textMessage: `Check not found: ${createdNode.title}` });
              logger.error(`[ImportTextModal] Check not found: ${createdNode.title}`);
              break;
            }
            const checkEntityType = EpisodeTextImportHelper.getCheckEntityTypes(step.commands);
            switch (checkEntityType) {
              case 'single': {
                const {
                  singleCheckTypeTrueGoto,
                  singleCheckTypeFalseGoto,
                } = EpisodeTextImportHelper.getSingleCheckCommandValues(step.commands);

                const trueGoto = allNodes.find((el) => el.title?.toLocaleLowerCase().trim() === singleCheckTypeTrueGoto);
                const falseGoto = allNodes.find((el) => el.title?.toLocaleLowerCase().trim() === singleCheckTypeFalseGoto);

                const stepSwitch: StepCheckSwitch[] = step.switch.map((el) => {
                  if (el.gotoBranchId < 1) {
                    return {
                      value: el.value,
                      gotoBranchId: el.value && trueGoto ? trueGoto.id : falseGoto?.id ?? -1,
                    };
                  }
                  return el;
                });

                const stepCheck = step.check;
                const checkTargetMemory = memoryBank?.find((m) => Number(m.id) === Number(stepCheck.variableId));
                let resolvedCheck = stepCheck;
                if (
                  checkTargetMemory?.type === MemoryType.Character
                  && typeof stepCheck.value === 'string'
                ) {
                  const matchedCharacter = characters?.find(
                    (c) => c.alias?.toLowerCase() === String(stepCheck.value).toLowerCase(),
                  );
                  if (matchedCharacter) {
                    resolvedCheck = { ...stepCheck, value: String(matchedCharacter.id) };
                  }
                }
                stepToCreate = {
                  ...stepToCreate,
                  stepTypeId,
                  check: resolvedCheck,
                  switch: stepSwitch,
                };
                stepsToCreate.push(stepToCreate);
                // eslint-disable-next-line no-continue
                continue;
              }
              case 'multi': {
                const {
                  multiCheckTypeGotoValues,
                } = EpisodeTextImportHelper.getMultiCheckCommandValues(step.commands);

                const stepSwitch: StepCheckSwitch[] = step.switch.map((el, index) => {
                  if (el.gotoBranchId < 1) {
                    const gotoBranch = allNodes.find((createdNodeEl) => createdNodeEl.title.toLocaleLowerCase().trim() === multiCheckTypeGotoValues[index]);
                    return {
                      value: el.value,
                      gotoBranchId: gotoBranch?.id ?? -1,
                    };
                  }
                  return el;
                });

                stepToCreate = {
                  ...stepToCreate,
                  stepTypeId,
                  check: step.check,
                  switch: stepSwitch,
                };
                stepsToCreate.push(stepToCreate);
                // eslint-disable-next-line no-continue
                continue;
              }
              case 'playerHas': {
                const {
                  trueGoto: playerHasTrueGoto,
                  falseGoto: playerHasFalseGoto,
                } = EpisodeTextImportHelper.getPlayerHasCheckCommandValues(step.commands);

                const trueGotoBranch = allNodes.find((el) => el.title?.toLocaleLowerCase().trim() === playerHasTrueGoto);
                const falseGotoBranch = allNodes.find((el) => el.title?.toLocaleLowerCase().trim() === playerHasFalseGoto);

                const stepSwitch: StepCheckSwitch[] = step.switch.map((el) => {
                  if (el.gotoBranchId < 1) {
                    return {
                      value: el.value,
                      gotoBranchId: el.value && trueGotoBranch ? trueGotoBranch.id : falseGotoBranch?.id ?? -1,
                    };
                  }
                  return el;
                });

                stepToCreate = {
                  ...stepToCreate,
                  stepTypeId,
                  check: step.check,
                  switch: stepSwitch,
                };
                stepsToCreate.push(stepToCreate);
                // eslint-disable-next-line no-continue
                continue;
              }
              case 'conditionList': {
                // Extract goto node names from conditionListItems
                const conditionGotoNodeNames = step.conditionListItems.map((condition) => {
                  const gotoMatch = condition.description.match(/#(?:GOTO|->)\s+(\w+)/i);
                  return gotoMatch?.[1]?.toLowerCase().trim() ?? '';
                });

                const stepSwitch: StepCheckSwitch[] = step.switch.map((el, index) => {
                  // Explicitly exclude 'value' field for conditionList
                  const { value, ...restEl } = el;
                  if (el.gotoBranchId < 1) {
                    const gotoNodeName = conditionGotoNodeNames[index];
                    const gotoBranch = allNodes.find((createdNodeEl) => createdNodeEl.title.toLocaleLowerCase().trim() === gotoNodeName);
                    return {
                      ...restEl,
                      gotoBranchId: gotoBranch?.id ?? -1,
                    };
                  }
                  return restEl;
                });

                // Explicitly exclude 'value' field from check for conditionList
                const { value: checkValue, ...restCheck } = step.check;
                stepToCreate = {
                  ...stepToCreate,
                  stepTypeId,
                  check: restCheck,
                  switch: stepSwitch,
                };
                stepsToCreate.push(stepToCreate);
                // eslint-disable-next-line no-continue
                continue;
              }
              default:
                break;
            }
            break;
          }

          default:
            stepToCreate = {
              ...stepToCreate,
              stepTypeId,
              text: speakerText,
            };
            break;
        }

        const gotoNodeCommand = step.commands.get(EpisodeTextImportCommandKey.Goto);

        if (gotoNodeCommand) {
          const toNodeTitle = gotoNodeCommand.values().next().value?.[2];
          const toNode = allNodes.find((el) => el.title === toNodeTitle);
          if (!toNode) {
            logger.error('[ImportTextModal] To node not found');
            // eslint-disable-next-line no-continue
            continue;
          }
          nodeToUpdate = {
            ...nodeToUpdate,
            gotoBranchId: toNode.id,
          };
        } else if (isLastStep && !isLastNode && !isEnding) {
          const nextNode = createdNodes[nodeIndex + 1];
          nodeToUpdate = {
            ...nodeToUpdate,
            gotoBranchId: nextNode.id,
          };
        }

        if (!isEmpty(stepToCreate)) {
          stepsToCreate.push(stepToCreate);
        }
      }

      if (stepsToCreate.length > 0) {
        nodeToUpdate = {
          ...nodeToUpdate,
          steps: stepsToCreate as BranchStep[],
        };
      }

      resolvedNodes.push({ nodeToUpdate, createdNodeId: createdNode.id });
    }

    /*
      Reposition all nodes by complete tree
     */
    const nonIntroNodes = resolvedNodes.filter(
      (entry) => entry.nodeToUpdate.title?.toLowerCase() !== 'intro',
    );

    const positionOverrides: { [key: number]: { x: number; y: number } } = {};

    if (nonIntroNodes.length > 0) {
      const branchesForTree = nonIntroNodes.map((entry) => entry.nodeToUpdate) as Branch[];
      const tree = createNodeTree(branchesForTree);
      const positions = EpisodeTextImportHelper.assignPositions(tree, branchesForTree[0].id, {
        x: 0,
        y: lowestNodeY + 350,
      });

      // Shift all positions so minimum x starts at a reasonable offset (100px from left)
      const positionValues = Object.values(positions);
      const minX = positionValues.length > 0 ? Math.min(...positionValues.map((p) => p.x)) : 0;
      const xShift = minX < 100 ? 100 - minX : 0;

      nonIntroNodes.forEach((entry) => {
        const n = entry.nodeToUpdate;
        if (n.id) {
          const position = positions[n.id];
          if (position) {
            positionOverrides[n.id] = { x: position.x + xShift, y: position.y };
          } else {
            positionOverrides[n.id] = { x: n.x ?? 0, y: firstUnlinkedNodePositionY };
            firstUnlinkedNodePositionY += 350;
          }
        }
      });
    }

    resolvedNodes.forEach((entry) => {
      const override = entry.nodeToUpdate.id ? positionOverrides[entry.nodeToUpdate.id] : undefined;
      const finalNode = override ? { ...entry.nodeToUpdate, ...override } : entry.nodeToUpdate;
      updateNodesPromises.push(
        async () => await apiService.updateEpisodeNode(episodeId, entry.createdNodeId, { ...finalNode }),
      );
      updatedNodes.push(finalNode as Branch);
    });

    try {
      const updatedNodesPromises = updateNodesPromises.map(
        (updateNodePromise) => (callback: (error: unknown | null, result: Partial<Branch> | null) => void) => {
          updateNodePromise()
            .then((response) => {
              callback(null, response);
            })
            .catch((error) => callback(error, null));
        },
      );
      await async.parallelLimit(updatedNodesPromises, MAX_CONCURRENT_PUT_REQUESTS);
    } catch (error) {
      logger.error(`Error in updating branches: ${error}`);
      showToast({ textMessage: `Error in updating branches: ${error}` });
    }

    return updatedNodes;
  }

  return { createEpisodeNodes, updateEpisodeNodes };
}
